// SSO redirects to the customer portal. Convorcher's admin web has no
// embedded login form — when the user isn't authenticated, we bounce them
// to portal.avataros.xyz/login?return_to=<our URL>. The portal validates
// return_to against its origin allowlist before navigating back.
//
// The portal URL is fetched at app boot from `GET /api/config`, which the
// Go server emits from its PORTAL_URL env var. Runtime config (not a
// build-time bake) so the same Docker image deploys to every env and
// Coolify env vars drive per-env behavior.

let portalUrl = ''

export function setPortalUrl(url: string | undefined): void {
  portalUrl = (url ?? '').replace(/\/$/, '')
}

export function getPortalUrl(): string {
  return portalUrl
}

interface RuntimeConfig {
  portal_url?: string
}

// loadPortalConfig fetches /api/config and applies the portal URL via
// setPortalUrl. Failures are non-fatal: we log and leave portalUrl empty —
// the SPA still mounts, cookie-authenticated users still see the app, and
// only the SSO-redirect code paths log a separate "portal URL not
// configured" error if they fire. Better than blocking the whole app on a
// transient backend issue.
export async function loadPortalConfig(): Promise<void> {
  try {
    const res = await fetch('/api/config', { credentials: 'include' })
    if (!res.ok) {
      console.error(`[portal] /api/config returned ${res.status}; SSO redirects disabled`)
      return
    }
    const data = (await res.json()) as RuntimeConfig
    setPortalUrl(data.portal_url)
  } catch (err) {
    console.error('[portal] failed to load /api/config; SSO redirects disabled', err)
  }
}

function withReturn(path: string, returnTo: string): string {
  if (!portalUrl) return ''
  return `${portalUrl}${path}?return_to=${encodeURIComponent(returnTo)}`
}

// Login bounces back to the page the user was originally trying to reach.
export function loginRedirectUrl(currentHref: string = window.location.href): string {
  return withReturn('/login', currentHref)
}

// Logout always lands back on Convorcher's home (the current deep-link is
// gone post-logout anyway). Calling code shouldn't have to choose between
// "back to where I was" vs "back to home" — pick home for the cleaner UX.
export function logoutRedirectUrl(origin: string = window.location.origin): string {
  return withReturn('/logout', `${origin}/`)
}

export function redirectToLogin(): void {
  const url = loginRedirectUrl()
  if (!url) {
    console.error('[portal] portal URL not configured; cannot redirect to login')
    return
  }
  window.location.href = url
}

export function redirectToLogout(): void {
  const url = logoutRedirectUrl()
  if (!url) {
    console.error('[portal] portal URL not configured; cannot redirect to logout')
    return
  }
  window.location.href = url
}
